<template>
  <div class="w-full flex flex-col gap-4">
    <div
      v-for="group in visibleGroups"
      :key="group.key"
      class="rounded-[8px] border overflow-hidden"
      :class="isOpen(group.key) ? 'border-neutral-300' : 'border-neutral-200'"
    >
      <div
        class="h-[54px] flex flex-row items-center gap-3 ltr:pr-5 rtl:pl-5"
        :class="{ 'bg-gray-50': isOpen(group.key) }"
      >
        <button
          type="button"
          class="h-full flex-1 min-w-0 flex flex-row items-center gap-3 ltr:pl-5 rtl:pr-5 ltr:text-left rtl:text-right cursor-pointer outline-none focus-visible:bg-gray-100"
          :aria-expanded="isOpen(group.key) ? 'true' : 'false'"
          @click="toggleGroup(group.key)"
        >
          <span
            class="material-symbols-outlined text-[24px] text-gray-500 transition-all"
            :class="isOpen(group.key) ? 'rotate-90' : 'rtl:rotate-180'"
            aria-hidden="true"
          >
            keyboard_arrow_right
          </span>
          <span class="min-w-0 flex flex-row items-center gap-2">
            <span class="text-base leading-6 font-semibold text-gray-800">
              {{ groupLabel(group.key) }}
            </span>
            <span class="text-base leading-6 text-gray-500">
              {{ selectedCount(group) }}/{{ group.events.length }}
            </span>
          </span>
        </button>
        <checkbox-v2
          :checked="isGroupSelected(group)"
          :indeterminate="isGroupIndeterminate(group)"
          :disabled="readonly"
          :label="
            $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.SELECT_ALL')
          "
          :aria-label="
            $t(
              'INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.SELECT_ALL_GROUP',
              { group: groupLabel(group.key) }
            )
          "
          @change="toggleGroupEvents(group, $event)"
        />
      </div>
      <div v-if="isOpen(group.key)" class="pt-2">
        <label
          v-for="event in group.events"
          :key="event"
          class="h-14 px-3 mb-0 flex flex-row items-center justify-between gap-3 border-b border-gray-100 last:border-b-0 font-normal cursor-pointer"
        >
          <span>
            <span class="block text-sm leading-5 text-gray-800">
              {{ getEventLabel(event) }}
            </span>
            <span class="block mt-[5px] text-xs leading-[18px] text-gray-600">
              {{ getEventDescription(event) }}
            </span>
          </span>
          <checkbox-v2
            :checked="value.includes(event)"
            :disabled="readonly"
            @change="toggleEvent(event, $event)"
          />
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import webhookMixin from './webhookMixin';
import { WEBHOOK_EVENT_GROUPS } from '../OnlineCubedView/constants';

export default {
  name: 'WebhookEventGroups',
  mixins: [webhookMixin],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    events: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    initialOpenGroup: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      openGroups: this.initialOpenGroup ? [this.initialOpenGroup] : [],
    };
  },
  computed: {
    visibleGroups() {
      return WEBHOOK_EVENT_GROUPS.map(group => ({
        key: group.key,
        events: group.events.filter(event => this.events.includes(event)),
      })).filter(group => group.events.length);
    },
  },
  methods: {
    groupLabel(groupKey) {
      return this.$t(
        `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.GROUPS.${groupKey.toUpperCase()}`
      );
    },
    getEventDescription(event) {
      return this.$t(
        `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENT_DESCRIPTIONS.${event.toUpperCase()}`
      );
    },
    selectedCount(group) {
      return group.events.filter(event => this.value.includes(event)).length;
    },
    isGroupSelected(group) {
      return this.selectedCount(group) === group.events.length;
    },
    isGroupIndeterminate(group) {
      const selected = this.selectedCount(group);
      return selected > 0 && selected < group.events.length;
    },
    isOpen(groupKey) {
      return this.openGroups.includes(groupKey);
    },
    toggleGroup(groupKey) {
      this.openGroups = this.isOpen(groupKey)
        ? this.openGroups.filter(key => key !== groupKey)
        : [...this.openGroups, groupKey];
    },
    toggleEvent(event, checked) {
      if (checked) {
        this.$emit('input', [...this.value, event]);
        return;
      }
      this.$emit(
        'input',
        this.value.filter(item => item !== event)
      );
    },
    toggleGroupEvents(group, checked) {
      if (checked) {
        const added = group.events.filter(event => !this.value.includes(event));
        this.$emit('input', [...this.value, ...added]);
        return;
      }
      this.$emit(
        'input',
        this.value.filter(event => !group.events.includes(event))
      );
    },
  },
};
</script>
